import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { withMonogramContext } from "./MonogramContext";
import clsx from "clsx";
import { DRAWER_NAMES, OVERLAYS } from "./const";

function MonogramPhoto({
  chosenVariant,
  chosenFont,
  position,
  overlay,
  textValue,
  monogramBounds,
  chosenThreadColor,
  chosenDecorativeColor,
  drawer,
  loading,
  setLoading,
  svgBounds,
  chosenOutlineColor,
  chosenDropShadowColor,
}) {
  const textContainerRef = useRef();
  const svgRef = useRef();
  const textRef = useRef();
  const [fontSize, setFontSize] = useState({
    minFontSize: 0,
    maxFontSize: 0,
    hiddenMinFontSize: 0,
    hiddenMaxFontSize: 0,
  });

  const showText = chosenFont.has_font_file || !chosenFont.font_image;

  const iOSiPadOSSafari =
    navigator.platform.startsWith("iP") ||
    (navigator.platform.startsWith("Mac") && navigator.maxTouchPoints > 4) ||
    /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  const calculateFontSize = useCallback(() => {
    let { height: containerHeight, width: containerWidth } =
      textContainerRef.current.getBoundingClientRect();

    if (chosenFont?.decorative_svg) {
      const { height: svgHeight, width: svgWidth } = svgRef.current.getBoundingClientRect();
      containerHeight = svgHeight;
      containerWidth = svgWidth;
    }

    const text = textValue.length < 1 ? "SCL" : textValue;
    const heightPercent = chosenFont?.decorative_svg
      ? svgBounds.heightPercent
      : monogramBounds.heightPercent;
    const chideNodeWidths = Array.from(textRef.current?.childNodes || []).map(
      (node) => node.getBoundingClientRect().width
    );
    chideNodeWidths.sort((a, b) => a - b);
    const spanWidth = chideNodeWidths[Math.floor(chideNodeWidths.length / 2)];
    const spanOutOfBoundsAmount = (spanWidth * text.length) / containerWidth;

    const sensFactor =
      spanOutOfBoundsAmount > 1
        ? spanOutOfBoundsAmount + parseFloat(chosenFont.sensitivity_factor)
        : 1;

    // Choose the divisor depending on the font & container
    const lengthAdjustment = text.length > 0 ? containerWidth / sensFactor : containerWidth;
    const hiddenFontSizeNum = containerHeight * (heightPercent / 100);
    let fontSizeNum = containerHeight * (heightPercent / 100) * (lengthAdjustment / containerWidth);
    // Set a minimum font size.
    fontSizeNum = Math.max(fontSizeNum, chosenFont?.decorative_svg ? 5 : 12);

    setFontSize({
      minFontSize: `${fontSizeNum}px`,
      maxFontSize: `${fontSizeNum * chosenFont.middle_font_multiplier}px`,
      hiddenMinFontSize: `${hiddenFontSizeNum}px`,
      hiddenMaxFontSize: `${hiddenFontSizeNum * chosenFont.middle_font_multiplier}px`,
    });
  }, [textContainerRef, svgRef, svgBounds, monogramBounds, chosenFont, textValue]);

  const calculateHiddenFontSize = useCallback(() => {
    let { height: containerHeight } = textContainerRef.current.getBoundingClientRect();

    if (chosenFont?.decorative_svg) {
      const { height: svgHeight } = svgRef.current.getBoundingClientRect();
      containerHeight = svgHeight;
    }

    const heightPercent = chosenFont?.decorative_svg
      ? svgBounds.heightPercent
      : monogramBounds.heightPercent;
    const hiddenFontSizeNum = containerHeight * (heightPercent / 100);

    setFontSize((prevState) => ({
      ...prevState,
      hiddenMinFontSize: `${hiddenFontSizeNum}px`,
      hiddenMaxFontSize: `${hiddenFontSizeNum * chosenFont.middle_font_multiplier}px`,
    }));
  }, [textContainerRef, svgRef, monogramBounds, chosenFont]);

  useEffect(() => {
    if (!textContainerRef.current) return 0;

    const calcSize = () => {
      setTimeout(() => {
        calculateFontSize();
      }, 400);
    };

    window.addEventListener("resize", calcSize);

    if (textRef.current?.childNodes?.[0]?.getBoundingClientRect()?.width === 0) {
      calcSize();
    } else {
      calculateFontSize();
    }

    setTimeout(() => {
      setLoading(false);
    }, 500);

    return () => {
      window.removeEventListener("resize", calcSize);
    };
  }, [textContainerRef, monogramBounds, chosenFont, calculateFontSize, setLoading, loading]);

  useEffect(() => {
    setLoading(true);
    calculateHiddenFontSize();
    setTimeout(() => {
      calculateFontSize();
    }, 400);
    setTimeout(() => {
      setLoading(false);
    }, 500);
  }, [chosenFont]);

  useEffect(() => {
    if (!chosenFont?.allow_border_color && window.location.href.includes("outline")) {
      // remove border from url
      const url = new URL(window.location.href);
      url.searchParams.delete("outline");
      window.history.replaceState({}, "", url);
    }
  }, [chosenFont]);

  const outlineWidth = window.matchMedia("(max-width: 600px)").matches
    ? (chosenFont?.outline_width || 3) / 2
    : chosenFont?.outline_width || 3;

  const dropShadowWidth = window.matchMedia("(max-width: 600px)").matches ? 3 : 6;

  const textStyle = useMemo(
    () =>
      showText && {
        fontFamily: chosenFont.font_name,
        fontSize: fontSize.minFontSize,
        lineHeight: fontSize.minFontSize,
        transition: "opacity 0.25s ease",
        opacity: fontSize ? 1 : 0,
        color: chosenThreadColor?.text_color || "black",
        textTransform: chosenFont?.is_capital_letters_only ? "uppercase" : "none",
        filter: chosenFont?.allow_drop_shadow
          ? `drop-shadow(${dropShadowWidth}px ${dropShadowWidth}px ${chosenDropShadowColor?.text_color})`
          : "none",
        WebkitTextStroke: chosenFont?.allow_border_color
          ? `${outlineWidth}px ${chosenOutlineColor?.text_color}`
          : "0",
        ...(drawer === DRAWER_NAMES.Words && { whiteSpace: "pre" }),
      },
    [showText, chosenFont, fontSize, chosenThreadColor, chosenOutlineColor, chosenDropShadowColor]
  );

  // Renders text as 3 different spans: first letter, middle letters, last letter.
  // Fonts fall back to the main font if the first or last letter is not set in the font metaobject.
  const renderText = useCallback(() => {
    const text = textValue.length < 1 ? "SCL" : textValue;
    let firstLetter = text[0] || "";
    let middleLetters = text.length > 1 ? text.slice(1, text.length - 1) : "";
    let lastLetter = text.length > 1 ? text[text.length - 1] : "";

    /**
     * If Levi font characters are not under the same DOM element,
     * we have to manually set the middle character to uppercase
     * and modify the last character so that it shows correct third character.
     */
    if (!iOSiPadOSSafari && chosenFont.font_name === "Levi") {
      firstLetter = firstLetter.toLowerCase();
      middleLetters = middleLetters.toUpperCase();
      if (lastLetter) {
        const userLetters = "abcdefghijklmnopqrstuvwxyz";
        const leviLetters = "1234567890!@#$%^&*()_+{}|:";
        lastLetter = lastLetter.toLowerCase();
        if (userLetters.includes(lastLetter)) {
          lastLetter = leviLetters[userLetters.indexOf(lastLetter)];
        }
      }
    }

    if (firstLetter && lastLetter && !middleLetters) {
      middleLetters = lastLetter;
      lastLetter = "";
    }
    return (
      <div style={position.text}>
        <div
          ref={textRef}
          style={{
            ...textStyle,
            fontFamily: `"${chosenFont.font_name}-first-letter", "${chosenFont.font_name}"`,
            visibility: "hidden",
            position: "absolute",
            fontSize: fontSize.hiddenMinFontSize,
            whiteSpace: "nowrap",
            backgroundColor: "green",
            padding: 50,
            margin: -50,
            gap: `${chosenFont.letter_spacing || 0}%`,
          }}
        >
          {[...firstLetter, ...middleLetters, ...lastLetter].map((letter, i) => (
            <span
              style={{
                ...textStyle,
                fontFamily: `"${chosenFont.font_name}-first-letter", "${chosenFont.font_name}"`,
                fontSize: fontSize.hiddenMinFontSize,
              }}
              key={`${letter}-${i}`}
            >
              {letter}
            </span>
          ))}
        </div>
        <div
          data-font-family={chosenFont.font_name}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            whiteSpace: "nowrap",
            opacity: loading ? "0" : "1",
            transition: "opacity 0.25s ease",
            padding: 50,
            margin: -50,
            gap: `${chosenFont.letter_spacing || 0}%`,
            textIndent: `${iOSiPadOSSafari && chosenFont.font_name === "Levi" ? 1 : 0}px`,
          }}
        >
          {iOSiPadOSSafari && chosenFont.font_name === "Levi" && (
            <span
              style={{
                ...textStyle,
                ...(drawer === DRAWER_NAMES.Initials && { fontSize: fontSize.maxFontSize }),
                fontFamily: `"${chosenFont.font_name}"`,
                letterSpacing: "1px",
              }}
            >
              {firstLetter.toLowerCase() + middleLetters.toLowerCase() + lastLetter.toLowerCase()}
            </span>
          )}
          {!(iOSiPadOSSafari && chosenFont.font_name === "Levi") && (
            <>
              <span
                style={{
                  ...textStyle,
                  fontFamily: `"${chosenFont.font_name}-first-letter", "${chosenFont.font_name}"`,
                }}
              >
                {firstLetter}
              </span>
              <span
                style={{
                  ...textStyle,
                  ...(drawer === DRAWER_NAMES.Initials && { fontSize: fontSize.maxFontSize }),
                  fontFamily: `"${chosenFont.font_name}"`,
                }}
              >
                {middleLetters}
              </span>
              <span
                style={{
                  ...textStyle,
                  fontFamily: `"${chosenFont.font_name}-last-letter", "${chosenFont.font_name}"`,
                }}
              >
                {lastLetter}
              </span>
            </>
          )}
        </div>
      </div>
    );
  }, [textStyle, chosenFont, textValue, position]);

  return (
    <div
      className={clsx(
        "monogram__photo-panel",
        overlay !== OVERLAYS.Monogram && "monogram__photo-panel--exit-intent"
      )}
    >
      <div className="monogram__photo-container" id="monogram-wrapper">
        <div
          style={{
            zIndex: 2,
            display: "block",
            position: "absolute",
            ...monogramBounds.styles.bounds,
          }}
          ref={textContainerRef}
        >
          {showText ? (
            <>
              {chosenFont.decorative_svg && (
                <div
                  style={{
                    zIndex: 1,
                    display: "block",
                    position: "absolute",
                    color: chosenDecorativeColor?.text_color || "black",
                    ...svgBounds.styles.bounds,
                  }}
                  ref={svgRef}
                  dangerouslySetInnerHTML={{ __html: chosenFont.decorative_svg }}
                ></div>
              )}
              {renderText()}
            </>
          ) : (
            <img
              src={chosenFont.font_image}
              style={{ ...position.image, ...monogramBounds.styles.monogram }}
            />
          )}
        </div>
        <img src={chosenVariant.featured_image} />
      </div>
    </div>
  );
}

export default withMonogramContext(MonogramPhoto);
